// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';
import {
  FileText, Receipt, CreditCard, Search, Plus, Send, Eye, ChevronDown, DollarSign, AlertTriangle,
  CheckCircle, XCircle, RefreshCw, Mail, Code, Download
} from 'lucide-react';
import api from '@/lib/api';
import { exportToCSV } from '@/components/crm/csv-export';
import { CRM_BASE } from './types';
import type { InstallmentPlanRow } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterContract as Contract,
  CrmMasterInvoice as Invoice,
  CrmMasterInvoicesDashboard as DashboardData,
  CrmMasterInvoice as InvoiceDetailData,
  CrmMasterFicSyncLog as FicSyncLog,
  CrmMasterSdiErrorRow as SdiError,
  CrmMasterInvoiceCheck as InvoiceCheckWarning,
} from '@easyflow/shared';
import {
  Tip, sdiStatusBadge, SelectField, Badge, ModalWrapper, AdvFilters,
  EMPTY_ADV_FILTERS, countAdvFilters, FilteredBadge, advFiltersToParams, AdvancedFilters
} from './shared';

export function TabFatturazione() {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState('');
  const [saving, setSaving] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailData | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [creditNoteTarget, setCreditNoteTarget] = useState<Invoice | null>(null);
  const [creditNoteReason, setCreditNoteReason] = useState('');
  const [creditNoteSaving, setCreditNoteSaving] = useState(false);
  const [ficSyncLogs, setFicSyncLogs] = useState<FicSyncLog[]>([]);
  const [sdiErrors, setSdiErrors] = useState<SdiError[]>([]);
  const [showLogs, setShowLogs] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  
  const [invDashboard, setInvDashboard] = useState<DashboardData | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [advFilters, setAdvFilters] = useState<AdvFilters>(EMPTY_ADV_FILTERS);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' });
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('type', typeFilter);
      const qs = params.toString();
      const dashQs = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' }).toString();
      const results = await Promise.allSettled([
        api.get<Invoice[] | { invoices?: Invoice[] }>(`${CRM_BASE}/invoices${qs ? `?${qs}` : ''}`),
        api.get<FicSyncLog[]>(`${CRM_BASE}/fic/sync-logs?limit=20`),
        api.get<{ sdi_errors?: SdiError[] }>(`${CRM_BASE}/sync-errors?type=sdi`),
        api.get<DashboardData>(`${CRM_BASE}/invoices/dashboard${dashQs ? `?${dashQs}` : ''}`),
      ]);
      const [invRes, logsRes, errRes, dashRes] = results;
      if (invRes.status === 'fulfilled' && invRes.value?.success && invRes.value.data) setInvoices(Array.isArray(invRes.value.data) ? invRes.value.data : (invRes.value.data).invoices || []);
      if (logsRes.status === 'fulfilled' && logsRes.value?.success && logsRes.value.data) setFicSyncLogs(Array.isArray(logsRes.value.data) ? logsRes.value.data : []);
      if (errRes.status === 'fulfilled' && errRes.value?.success && errRes.value.data) setSdiErrors((errRes.value.data).sdi_errors || []);
      if (dashRes.status === 'fulfilled' && dashRes.value?.success && dashRes.value.data) setInvDashboard(dashRes.value.data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, advFilters]);

  useEffect(() => { void loadInvoices(); }, [loadInvoices]);

  const selectedContract = contracts.find((c) => c.id === selectedContractId);
  const contractTotal = selectedContract ? Number(selectedContract.total_gross ?? selectedContract.quote_total_gross ?? 0) : 0;

  const openCreate = async () => {
    const res = await api.get(`${CRM_BASE}/contracts`);
    if (res.success && res.data) {
      const all: Contract[] = Array.isArray(res.data) ? res.data : (res.data as { contracts?: Contract[] }).contracts || [];
      setContracts(all.filter((c) => c.status === 'signed'));
    }
    setSelectedContractId('');
    setShowCreate(true);
  };

  const [invoiceCheckWarning, setInvoiceCheckWarning] = useState<InvoiceCheckWarning | null>(null);

  const doCreateInvoice = async (force = false) => {
    setSaving(true);
    try {
      const res = await api.post<{ count?: number; total_gross?: number | string }>(`${CRM_BASE}/invoices/from-contract/${selectedContractId}`, { force });
      const resX = res as { duplicate?: InvoiceCheckWarning; multiple?: boolean };
      if (!res.success) {
        if (resX.duplicate && !force) {
          setInvoiceCheckWarning(resX.duplicate);
          return;
        }
        throw new Error(res.error || 'Errore nella creazione della fattura');
      }
      setShowCreate(false);
      void loadInvoices();

      if (resX.multiple) {
        toast({ title: 'Fatture create', description: `${res.data?.count || '?'} fatture create (una per rata) — totale € ${Number(res.data?.total_gross || '0').toFixed(2)}. Verranno emesse automaticamente dopo ogni pagamento.` });
      } else {
        toast({ title: 'Fattura creata', description: 'Fattura creata. Verrà emessa automaticamente dopo il pagamento.' });
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore creazione fattura', description: __err?.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedContractId) return;
    try {
      const checkRes = await api.get<InvoiceCheckWarning & { existing?: boolean }>(`${CRM_BASE}/invoices/check-contract/${selectedContractId}`);
      if (checkRes.success && (checkRes.data)?.existing) {
        setInvoiceCheckWarning(checkRes.data);
        return;
      }
    } catch {}
    void doCreateInvoice();
  };

  const viewDetail = async (id: string) => {
    const res = await api.get<InvoiceDetailData>(`${CRM_BASE}/invoices/${id}`);
    if (res.success && res.data) setInvoiceDetail(res.data);
  };

  const handleSendSdi = async (id: string) => {
    if (!confirm('Inviare questa fattura al Sistema di Interscambio (SDI)?')) return;
    setActionLoading(id);
    try {
      await api.post(`${CRM_BASE}/invoices/${id}/send-sdi`);
      void loadInvoices();
    } finally {
      setActionLoading(null);
    }
  };

  const openCreditNote = (inv: Invoice) => {
    setCreditNoteTarget(inv);
    setCreditNoteReason('');
  };

  const submitCreditNote = async () => {
    if (!creditNoteTarget || !creditNoteReason.trim()) return;
    setCreditNoteSaving(true);
    try {
      await api.post(`${CRM_BASE}/invoices/${creditNoteTarget.id}/credit-note`, { reason: creditNoteReason.trim() });
      setCreditNoteTarget(null);
      setCreditNoteReason('');
      void loadInvoices();
    } finally {
      setCreditNoteSaving(false);
    }
  };

  const handleCheckSdiStatus = async (id: string) => {
    setActionLoading(id);
    try {
      await api.post(`${CRM_BASE}/invoices/${id}/check-sdi-status`);
      void loadInvoices();
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncFic = async (id: string) => {
    if (!confirm('Sincronizzare questa fattura su Fatture in Cloud, inviare allo SDI e inviare email al cliente?')) return;
    setActionLoading(id);
    try {
      const res = await api.post<{ ficDocumentId?: string | number; sdiStatus?: string; emailSent?: boolean }>(`${CRM_BASE}/invoices/${id}/sync-fic`);
      const data = res?.data;
      toast({ title: 'Fattura sincronizzata', description: `FiC Doc #${data?.ficDocumentId || '?'} — SDI: ${data?.sdiStatus || '?'} — Email: ${data?.emailSent ? 'Inviata' : 'Non inviata'}` });
      void loadInvoices();
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore sincronizzazione', description: (__err?.error as string | undefined) || __err?.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendEmail = async (id: string) => {
    if (!confirm('Inviare la fattura via email al cliente?')) return;
    setActionLoading(id);
    try {
      const res = await api.post(`${CRM_BASE}/invoices/${id}/send-email`);
      toast({ title: 'Email inviata', description: res?.message || 'Fattura inviata con successo' });
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore invio email', description: (__err?.error as string | undefined) || __err?.message || 'Errore sconosciuto', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const ficActionLabel: Record<string, string> = {
    create_invoice: 'Creazione Fattura',
    create_credit_note: 'Nota di Credito',
    send_sdi: 'Invio SDI',
    check_status: 'Verifica Stato',
  };

  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>({});

  const toggleClient = (clientKey: string) => {
    setExpandedClients(prev => ({ ...prev, [clientKey]: !prev[clientKey] }));
  };

  const filteredInvoices = invoices.filter(inv => {
    if (searchFilter && !(inv.tenant_name || '').toLowerCase().includes(searchFilter.toLowerCase()) && !(inv.invoice_number || '').toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  const filtersActive = countAdvFilters(advFilters) > 0 || !!searchFilter || !!statusFilter || !!typeFilter;

  const invoiceKpis = React.useMemo(() => {
    const isPaid = (inv: Invoice): boolean => inv.status === 'paid' || inv.fic_sdi_status === 'accepted_sdi';
    const isOverdue = (inv: Invoice): boolean => inv.status === 'overdue';
    let totalValue = 0;
    let paidValue = 0;
    let overdueValue = 0;
    for (const inv of filteredInvoices) {
      const gross = Number(inv.total_gross || '0');
      totalValue += gross;
      if (isPaid(inv)) paidValue += gross;
      if (isOverdue(inv)) overdueValue += gross;
    }
    return {
      count: filteredInvoices.length,
      totalValue,
      paidValue,
      overdueValue,
    };
  }, [filteredInvoices]);

  const groupedByClient = React.useMemo(() => {
    const groups: Record<string, { name: string; invoices: Invoice[]; totalGross: number; paidCount: number; draftCount: number; overdueCount: number }> = {};
    filteredInvoices.forEach(inv => {
      const key = inv.tenant_name || inv.pipeline_entry_id || inv.tenant_id || 'Sconosciuto';
      if (!groups[key]) groups[key] = { name: key, invoices: [], totalGross: 0, paidCount: 0, draftCount: 0, overdueCount: 0 };
      groups[key].invoices.push(inv);
      groups[key].totalGross += Number(inv.total_gross || '0');
      if (inv.status === 'paid' || inv.fic_sdi_status === 'accepted_sdi') groups[key].paidCount++;
      else if (inv.status === 'draft') groups[key].draftCount++;
      else if (inv.status === 'overdue') groups[key].overdueCount++;
    });
    Object.values(groups).forEach(g => {
      g.invoices.sort((a, b) => new Date(a.issue_date || 0).getTime() - new Date(b.issue_date || 0).getTime());
    });
    return Object.values(groups).sort((a, b) => b.totalGross - a.totalGross);
  }, [filteredInvoices]);

  const downloadInvoicePdf = async (invId: string, invNumber: string) => {
    try {
      const token = localStorage.getItem('edgvoip_master_token') || localStorage.getItem('master_token') || localStorage.getItem('token');
      const response = await fetch(`/api${CRM_BASE}/invoices/${invId}/pdf`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Errore scaricamento PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fattura_${invNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'Errore download PDF', description: __err?.message || 'Errore', variant: 'destructive' });
    }
  };

  const downloadInvoiceXml = async (invId: string, invNumber: string) => {
    try {
      const token = localStorage.getItem('edgvoip_master_token') || localStorage.getItem('master_token') || localStorage.getItem('token');
      const response = await fetch(`/api${CRM_BASE}/invoices/${invId}/xml`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.error || 'Errore scaricamento XML');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Fattura_${invNumber}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: 'XML non disponibile', description: __err?.message || 'Errore', variant: 'destructive' });
    }
  };

  const statusLabel: Record<string, string> = {
    draft: 'Bozza', issued: 'Emessa', sent_sdi: 'Inviata SDI', accepted_sdi: 'Accettata SDI',
    rejected_sdi: 'Rifiutata SDI', sdi_error: 'Errore SDI', paid: 'Pagata', partially_paid: 'Parz. Pagata',
    overdue: 'Scaduta', cancelled: 'Annullata', credited: 'Stornata'
  };

  const handleExportCSV = () => {
    exportToCSV(filteredInvoices as unknown as Record<string, unknown>[], 'fatture', [
      { key: 'invoice_number', label: 'Numero' },
      { key: 'tenant_name', label: 'Cliente' },
      { key: 'type', label: 'Tipo' },
      { key: 'total_gross', label: 'Totale' },
      { key: 'status', label: 'Stato' },
      { key: 'fic_sdi_status', label: 'SDI' },
      { key: 'issue_date', label: 'Emissione' },
    ]);
  };

  return (
    <div className="space-y-4">
      {countAdvFilters(advFilters) > 0 && (
        <div className="flex justify-end"><FilteredBadge filters={advFilters} /></div>
      )}
      {invDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: filtersActive ? 'Fatture (filtrate)' : 'Totale Fatture', value: filtersActive ? invoiceKpis.count : invDashboard.total_invoices, icon: Receipt, color: 'text-blue-400', fmt: 'num' },
            { label: filtersActive ? 'Valore (filtrato)' : 'Valore Totale', value: filtersActive ? invoiceKpis.totalValue : invDashboard.total_value, icon: DollarSign, color: 'text-green-400', fmt: 'eur' },
            { label: 'Incassato', value: filtersActive ? invoiceKpis.paidValue : invDashboard.paid_value, icon: CheckCircle, color: 'text-emerald-400', fmt: 'eur' },
            { label: 'Scaduto', value: filtersActive ? invoiceKpis.overdueValue : invDashboard.overdue_value, icon: AlertTriangle, color: 'text-red-400', fmt: 'eur' },
          ].map((kpi, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">{kpi.label}</span>
                <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
              </div>
              <p className="text-2xl font-bold text-foreground dark:text-white">
                {kpi.fmt === 'eur' ? `€ ${Number(kpi.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : kpi.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {invDashboard && (invDashboard.credit_notes_count > 0 || invDashboard.this_month_count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4">
            <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Questo Mese</span>
            <p className="text-lg font-bold text-foreground dark:text-white">{invDashboard.this_month_count} fatture - € {Number(invDashboard.this_month_value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4">
            <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Note di Credito</span>
            <p className="text-lg font-bold text-orange-400">{invDashboard.credit_notes_count} - € {Number(invDashboard.credit_notes_value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
          </div>
          {invDashboard.sdi_stats && Object.keys(invDashboard.sdi_stats).length > 0 && (
            <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4">
              <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato SDI</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {Object.entries(invDashboard.sdi_stats).map(([k, v]) => (
                  <Badge key={k} className={sdiStatusBadge[k] || 'bg-muted/500/20 text-muted-foreground/70'}>{k}: {v}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {sdiErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <button onClick={() => setShowErrors(!showErrors)} className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <h3 className="text-red-700 dark:text-red-400 font-semibold">Errori Emissione Fatture ({sdiErrors.length})</h3>
            </div>
            <ChevronDown className={`h-4 w-4 text-red-500 transition-transform ${showErrors ? 'rotate-180' : ''}`} />
          </button>
          {showErrors && (
            <div className="mt-3 space-y-2">
              {sdiErrors.map((err) => (
                <div key={err.id} className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800/50 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <span className="text-foreground dark:text-white text-sm font-medium">{err.invoice_number}</span>
                    <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm ml-3">{err.tenant_name}</span>
                    <div className="mt-1">
                      <Badge className="bg-red-500/20 text-red-400">{err.fic_sdi_status}</Badge>
                      <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs ml-2">€ {Number(err.total_gross || '0').toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Tip label="Reinvia SDI"><button onClick={() => handleSendSdi(err.id)} className="text-blue-400 hover:text-blue-300 p-1.5 rounded-lg hover:bg-blue-500/10 text-xs">
                      <Send className="h-4 w-4" />
                    </button></Tip>
                    <Tip label="Verifica stato"><button onClick={() => handleCheckSdiStatus(err.id)} className="text-muted-foreground/70 hover:text-muted-foreground/60 p-1.5 rounded-lg hover:bg-muted/500/10">
                      <RefreshCw className="h-4 w-4" />
                    </button></Tip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
            <input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Cerca fattura..."
              className="pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm w-48" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="issued">Emessa</option>
            <option value="sent_sdi">Inviata SDI</option>
            <option value="accepted_sdi">Accettata SDI</option>
            <option value="rejected_sdi">Scartata SDI</option>
            <option value="credited">Stornata</option>
            <option value="paid">Pagata</option>
            <option value="partially_paid">Parz. Pagata</option>
            <option value="overdue">Scaduta</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti i tipi</option>
            <option value="invoice">Fattura</option>
            <option value="credit_note">Nota di Credito</option>
            <option value="proforma">Proforma</option>
          </select>
          <AdvancedFilters filters={advFilters} onChange={setAdvFilters} dateLabel="Data emissione" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportCSV} className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => setShowLogs(!showLogs)}
            className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
            <FileText className="h-4 w-4" /> Log Sync
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Fattura da Contratto
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Caricamento...</div>
        ) : groupedByClient.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Nessuna fattura</div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {groupedByClient.map((group) => {
              const isExpanded = expandedClients[group.name];
              return (
                <div key={group.name}>
                  <button onClick={() => toggleClient(group.name)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 dark:hover:bg-gray-700/50 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <ChevronDown className={`h-4 w-4 text-muted-foreground/70 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      <span className="text-foreground dark:text-white font-semibold text-sm">{group.name}</span>
                      <Badge className="bg-blue-500/20 text-blue-400">{group.invoices.length} {group.invoices.length === 1 ? 'fattura' : 'fatture'}</Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        {group.paidCount > 0 && <Badge className="bg-green-500/20 text-green-400">{group.paidCount} pagate</Badge>}
                        {group.draftCount > 0 && <Badge className="bg-muted/500/20 text-muted-foreground/70">{group.draftCount} bozze</Badge>}
                        {group.overdueCount > 0 && <Badge className="bg-red-500/20 text-red-400">{group.overdueCount} scadute</Badge>}
                      </div>
                      <span className="text-foreground dark:text-white font-bold text-sm">€ {group.totalGross.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="bg-muted/50 dark:bg-gray-900/30 border-t border-border dark:border-gray-700">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border dark:border-gray-700/50">
                            <th className="text-left py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Numero</th>
                            <th className="text-left py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Tipo</th>
                            <th className="text-right py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Importo</th>
                            <th className="text-left py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Emissione</th>
                            <th className="text-left py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Stato</th>
                            <th className="text-left py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">FiC / SDI</th>
                            <th className="text-right py-2 px-4 text-muted-foreground dark:text-muted-foreground/70 text-xs font-medium">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.invoices.map((inv) => (
                            <tr key={inv.id} className="border-b border-gray-100 dark:border-gray-700/30 hover:bg-white dark:hover:bg-gray-800/50 transition-colors">
                              <td className="py-2.5 px-4 font-mono text-sm">
                                {inv.fic_number ? (
                                  <div className="flex flex-col leading-tight">
                                    <span className="text-foreground dark:text-white">{inv.fic_number}</span>
                                    <span className="text-[10px] text-muted-foreground/70 dark:text-muted-foreground">{inv.invoice_number}</span>
                                  </div>
                                ) : (
                                  <span className="text-foreground dark:text-white">{inv.invoice_number}</span>
                                )}
                              </td>
                              <td className="py-2.5 px-4">
                                <Badge className={inv.type === 'credit_note' ? 'bg-orange-500/20 text-orange-400' : inv.type === 'proforma' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                                  {inv.type === 'credit_note' ? 'NC' : inv.type === 'proforma' ? 'Proforma' : 'Fattura'}
                                </Badge>
                              </td>
                              <td className="py-2.5 px-4 text-right text-foreground dark:text-white text-sm font-medium">€ {Number(inv.total_gross || '0').toFixed(2)}</td>
                              <td className="py-2.5 px-4 text-muted-foreground dark:text-muted-foreground/60 text-sm">{inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('it-IT') : '-'}</td>
                              <td className="py-2.5 px-4">
                                <Badge className={sdiStatusBadge[inv.fic_sdi_status ?? ''] || sdiStatusBadge[inv.status ?? ''] || 'bg-muted/500/20 text-muted-foreground/70'}>
                                  {statusLabel[inv.fic_sdi_status || inv.status || ''] || inv.fic_sdi_status || inv.status || 'N/A'}
                                </Badge>
                              </td>
                              <td className="py-2.5 px-4">
                                {inv.fic_document_id ? (
                                  <Badge className="bg-green-500/20 text-green-400">FiC #{inv.fic_document_id}</Badge>
                                ) : inv.status === 'paid' || inv.fic_sdi_status === 'sdi_error' ? (
                                  <Badge className="bg-red-500/20 text-red-400">Sync fallita</Badge>
                                ) : (
                                  <Badge className="bg-muted/500/20 text-muted-foreground/70">In attesa</Badge>
                                )}
                              </td>
                              <td className="py-2.5 px-4">
                                <div className="flex items-center justify-end gap-1">
                                  <Tip label="Dettaglio"><button onClick={() => viewDetail(inv.id)} className="text-muted-foreground/70 hover:text-white p-1 rounded hover:bg-gray-600">
                                    <Eye className="h-3.5 w-3.5" />
                                  </button></Tip>
                                  {inv.fic_document_id && (
                                    <>
                                      <Tip label="Scarica PDF"><button onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number ?? '')}
                                        className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-500 hover:text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/30 hover:bg-emerald-500/10">
                                        <FileText className="h-3 w-3" />PDF
                                      </button></Tip>
                                      <Tip label="Scarica XML"><button onClick={() => downloadInvoiceXml(inv.id, inv.invoice_number ?? '')}
                                        className="inline-flex items-center gap-0.5 text-xs font-bold text-blue-500 hover:text-blue-400 px-1.5 py-0.5 rounded border border-blue-500/30 hover:bg-blue-500/10">
                                        <Code className="h-3 w-3" />XML
                                      </button></Tip>
                                      <Tip label="Invia Email"><button onClick={() => handleSendEmail(inv.id)} disabled={actionLoading === inv.id}
                                        className="inline-flex items-center gap-0.5 text-xs text-cyan-500 hover:text-cyan-400 px-1.5 py-0.5 rounded border border-cyan-500/30 hover:bg-cyan-500/10">
                                        <Mail className="h-3 w-3" />
                                      </button></Tip>
                                    </>
                                  )}
                                  {!inv.fic_document_id && (inv.status === 'paid' || inv.fic_sdi_status === 'sdi_error') && (
                                    <Tip label="Riprova Sync FiC"><button onClick={() => handleSyncFic(inv.id)} disabled={actionLoading === inv.id}
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-amber-500 hover:text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20">
                                      {actionLoading === inv.id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
                                      Retry
                                    </button></Tip>
                                  )}
                                  {(inv.status === 'issued' || inv.fic_sdi_status === 'issued') && inv.fic_document_id && inv.type !== 'credit_note' && inv.fic_sdi_status !== 'sent_sdi' && inv.fic_sdi_status !== 'accepted_sdi' && (
                                    <Tip label="Invia SDI"><button onClick={() => handleSendSdi(inv.id)} disabled={actionLoading === inv.id}
                                      className="inline-flex items-center gap-0.5 text-xs text-indigo-500 hover:text-indigo-400 px-1.5 py-0.5 rounded border border-indigo-500/30 hover:bg-indigo-500/10">
                                      <Send className="h-3 w-3" />SDI
                                    </button></Tip>
                                  )}
                                  {inv.fic_document_id && inv.fic_sdi_status && !['accepted_sdi', 'rejected_sdi'].includes(inv.fic_sdi_status) && (
                                    <Tip label="Aggiorna stato SDI"><button onClick={() => handleCheckSdiStatus(inv.id)} disabled={actionLoading === inv.id}
                                      className="text-muted-foreground/70 hover:text-purple-400 p-1 rounded hover:bg-purple-500/10">
                                      <RefreshCw className="h-3 w-3" />
                                    </button></Tip>
                                  )}
                                  {inv.type === 'invoice' && inv.status !== 'credited' && (inv.status === 'paid' || inv.fic_sdi_status === 'accepted_sdi' || inv.status === 'issued') && (
                                    <Tip label="Nota di Credito"><button onClick={() => openCreditNote(inv)} disabled={actionLoading === inv.id}
                                      className="text-muted-foreground/70 hover:text-orange-400 p-1 rounded hover:bg-orange-500/10">
                                      <XCircle className="h-3.5 w-3.5" />
                                    </button></Tip>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showLogs && (
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4">
          <h3 className="text-foreground dark:text-white font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" /> Log Sincronizzazione Fatture in Cloud
          </h3>
          {ficSyncLogs.length === 0 ? (
            <p className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Nessun log di sincronizzazione</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {ficSyncLogs.map((log, i: number) => (
                <div key={log.id || i} className="flex items-center gap-3 py-1.5 px-2 rounded-lg bg-muted/50 dark:bg-gray-700/50 text-sm">
                  <Badge className={log.status === 'success' ? 'bg-green-500/20 text-green-400' : log.status === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                    {log.status}
                  </Badge>
                  <span className="text-muted-foreground dark:text-muted-foreground/60">{ficActionLabel[log.action ?? ''] || log.action}</span>
                  <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{log.entity_type} {log.entity_id?.substring(0, 8)}</span>
                  {log.fic_id && <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">FiC #{log.fic_id}</span>}
                  {log.error_message && <span className="text-red-400 text-xs truncate max-w-[200px]" title={log.error_message}>{log.error_message}</span>}
                  <span className="text-muted-foreground/70 text-xs ml-auto">{log.synced_at ? new Date(log.synced_at).toLocaleString('it-IT') : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ModalWrapper open={showCreate} onClose={() => setShowCreate(false)} title="Crea Fattura da Contratto Firmato" wide>
        <div className="space-y-4">
          <SelectField label="Seleziona Contratto Firmato" value={selectedContractId} onChange={setSelectedContractId}
            options={[{ value: '', label: 'Seleziona...' }, ...contracts.map(c => ({ value: c.id, label: `${c.contract_number} - ${c.tenant_name || ''} - € ${Number(c.total_gross || c.quote_total_gross || 0).toFixed(2)}` }))]} />

          {selectedContractId && selectedContract && (
            <div className="bg-muted/50 dark:bg-gray-700/30 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-foreground dark:text-white font-semibold flex items-center gap-2">
                  <CreditCard className="h-4 w-4" /> Piano Pagamento dal Contratto
                </h4>
                <span className="text-muted-foreground dark:text-muted-foreground/60 text-sm font-medium">Totale IVA incl.: € {contractTotal.toFixed(2)}</span>
              </div>

              {(() => {
                let plan: InstallmentPlanRow[] | null = null;
                try {
                  let raw = selectedContract.installment_plan
                    ? (typeof selectedContract.installment_plan === 'string' ? JSON.parse(selectedContract.installment_plan) : selectedContract.installment_plan)
                    : null;
                  if (raw && !Array.isArray(raw) && raw.installments) raw = raw.installments;
                  if (raw && Array.isArray(raw)) plan = raw;
                } catch { /* ignore bad data */ }
                if (plan && plan.length > 1) {
                  return (
                    <div>
                      <p className="text-muted-foreground dark:text-muted-foreground/60 text-sm mb-2">
                        Verranno create <strong>{plan.length} fatture</strong> (una per rata) come da piano contrattuale:
                      </p>
                      <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-600 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border dark:border-gray-700 bg-muted/50 dark:bg-gray-700/50">
                              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 font-medium">Rata</th>
                              <th className="text-right py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 font-medium">Importo</th>
                              <th className="text-left py-2 px-3 text-muted-foreground dark:text-muted-foreground/70 font-medium">Scadenza</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plan.map((inst) => (
                              <tr key={inst.number} className="border-b border-gray-100 dark:border-gray-700/50">
                                <td className="py-2 px-3 text-foreground dark:text-white">#{inst.number}</td>
                                <td className="py-2 px-3 text-right text-foreground dark:text-white font-medium">€ {Number(inst.amount).toFixed(2)}</td>
                                <td className="py-2 px-3 text-muted-foreground dark:text-muted-foreground/60">{new Date(inst.due_date || '').toLocaleDateString('it-IT')}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-muted/50 dark:bg-gray-700/50">
                              <td className="py-2 px-3 text-foreground dark:text-muted-foreground/60 font-semibold">Totale</td>
                              <td className="py-2 px-3 text-right font-semibold text-green-600 dark:text-green-400">
                                € {plan.reduce((s: number, i) => s + Number(i.amount), 0).toFixed(2)}
                              </td>
                              <td className="py-2 px-3"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                } else {
                  const singleAmount = (plan && plan.length === 1) ? Number(plan[0]?.amount) : contractTotal;
                  const singleDate = (plan && plan.length === 1 && plan[0]?.due_date) ? new Date(plan[0].due_date).toLocaleDateString('it-IT') : null;
                  return (
                    <p className="text-muted-foreground dark:text-muted-foreground/60 text-sm">
                      Verrà creata <strong>1 fattura</strong> — pagamento unico di <strong>€ {singleAmount.toFixed(2)}</strong>
                      {singleDate && <> — scadenza {singleDate}</>}
                    </p>
                  );
                }
              })()}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={handleCreate} disabled={saving || !selectedContractId}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
              {saving ? 'Creazione...' : (() => {
                if (!selectedContract) return 'Crea Fattura';
                try {
                  let plan = selectedContract?.installment_plan
                    ? (typeof selectedContract.installment_plan === 'string' ? JSON.parse(selectedContract.installment_plan) : selectedContract.installment_plan)
                    : null;
                  if (plan && !Array.isArray(plan) && plan.installments) plan = plan.installments;
                  return (plan && Array.isArray(plan) && plan.length > 1) ? `Crea ${plan.length} Fatture` : 'Crea Fattura';
                } catch { return 'Crea Fattura'; }
              })()}
            </button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper open={!!creditNoteTarget} onClose={() => setCreditNoteTarget(null)} title={`Nota di Credito - ${creditNoteTarget?.invoice_number || ''}`}>
        <div className="space-y-4">
          <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground dark:text-muted-foreground/70">Cliente:</span> <span className="text-foreground dark:text-white">{creditNoteTarget?.tenant_name}</span></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70">Importo:</span> <span className="text-foreground dark:text-white">€ {Number(creditNoteTarget?.total_gross || '0').toFixed(2)}</span></div>
            </div>
          </div>
          <div>
            <label className="block text-foreground dark:text-muted-foreground/60 text-sm font-medium mb-1">Motivo della nota di credito *</label>
            <textarea value={creditNoteReason} onChange={(e) => setCreditNoteReason(e.target.value)}
              rows={3} placeholder="Indicare il motivo dello storno..."
              className="w-full bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
          </div>
          <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs">
            La nota di credito verrà creata su Fatture in Cloud (se configurato) e la fattura originale verrà marcata come stornata.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setCreditNoteTarget(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
            <button onClick={submitCreditNote} disabled={creditNoteSaving || !creditNoteReason.trim()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
              {creditNoteSaving ? 'Emissione...' : 'Emetti Nota di Credito'}
            </button>
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper open={!!invoiceDetail} onClose={() => setInvoiceDetail(null)} title={`Dettaglio Fattura ${invoiceDetail?.invoice_number || ''}`} wide>
        {invoiceDetail && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Cliente:</span><p className="text-foreground dark:text-white">{invoiceDetail.tenant_name}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Tipo:</span><p className="text-foreground dark:text-white capitalize">{invoiceDetail.type === 'credit_note' ? 'Nota di Credito' : invoiceDetail.type}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Netto:</span><p className="text-foreground dark:text-white">€ {Number(invoiceDetail.subtotal_net || 0).toFixed(2)}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">IVA:</span><p className="text-foreground dark:text-white">€ {Number(invoiceDetail.total_vat || 0).toFixed(2)}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Lordo:</span><p className="text-foreground dark:text-white font-semibold">€ {Number(invoiceDetail.total_gross || '0').toFixed(2)}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Pagato:</span><p className="text-foreground dark:text-white">€ {Number(invoiceDetail.amount_paid || 0).toFixed(2)}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Scadenza:</span><p className="text-foreground dark:text-white">{invoiceDetail.due_date ? new Date(invoiceDetail.due_date).toLocaleDateString('it-IT') : '-'}</p></div>
              <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Stato SDI:</span><p className="text-foreground dark:text-white">{invoiceDetail.fic_sdi_status || invoiceDetail.status || '-'}</p></div>
              {invoiceDetail.fic_document_id && (
                <div><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">FiC ID:</span><p className="text-foreground dark:text-white">#{invoiceDetail.fic_document_id}</p></div>
              )}
              {invoiceDetail.credit_note_reason && (
                <div className="col-span-2"><span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">Motivo NC:</span><p className="text-orange-600 dark:text-orange-400">{invoiceDetail.credit_note_reason}</p></div>
              )}
            </div>
            {invoiceDetail.installments && invoiceDetail.installments.length > 0 && (
              <div>
                <h4 className="text-foreground dark:text-white font-medium mb-2">Rate</h4>
                <div className="space-y-2">
                  {invoiceDetail.installments.map((inst) => (
                    <div key={inst.id} className="bg-muted dark:bg-gray-700 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <span className="text-foreground dark:text-white text-sm">Rata {inst.installment_number}{inst.is_entry_fee ? ' (Entry Fee)' : ''}</span>
                        <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm ml-3">Scad: {new Date(inst.due_date ?? '').toLocaleDateString('it-IT')}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-foreground dark:text-white">€ {Number(inst.amount).toFixed(2)}</span>
                        <Badge className={inst.status === 'paid' ? 'bg-green-500/20 text-green-400' : inst.status === 'overdue' ? 'bg-red-500/20 text-red-400' : 'bg-muted/500/20 text-muted-foreground/70'}>
                          {inst.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </ModalWrapper>

      <ModalWrapper open={!!invoiceCheckWarning} onClose={() => setInvoiceCheckWarning(null)} title="Fattura Già Esistente">
        {invoiceCheckWarning && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-300 mb-2">Esistono già fatture per questo contratto</p>
                <p className="text-xs text-amber-400/80">Fattura: {invoiceCheckWarning.invoice_number || '-'} ({invoiceCheckWarning.installments_count || 1} rate)</p>
                <p className="text-xs text-amber-400/80">Totale fatturato: € {Number(invoiceCheckWarning.total_gross || '0').toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs text-amber-400/80">Pagato: € {Number(invoiceCheckWarning.paid_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })} ({invoiceCheckWarning.paid_count || 0} rate)</p>
                <p className="text-xs text-amber-400/60 mt-2">Vuoi creare una fattura aggiuntiva comunque?</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setInvoiceCheckWarning(null)} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={() => { setInvoiceCheckWarning(null); void doCreateInvoice(true); }}
                className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium">Procedi comunque</button>
            </div>
          </div>
        )}
      </ModalWrapper>
    </div>
  );
}

